import React, { useEffect, useState, useCallback, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import jsQR from "jsqr";
import {
  login,
  logout,
  getToken,
  getUserIdFromToken,
  getMyDispatch,
  getMyProfile,
  getMyDocuments,
  uploadMyDocument,
  downloadMyDocument,
  deleteMyDocument,
  getMyLiveLocation,
  getGeoNexSession,
  connectGeoNex,
  disconnectGeoNex,
  changeGeoNexPassword,
  geonexCheckin,
  geonexCheckout,
  getVehicleStatus,
  getPreoperationalForm,
  createPreoperational,
  uploadDefectEvidence,
  getMyMaintenance,
  createDriverMaintenance,
} from "./api";

import busBg from "./bus.webp";
import alasLogo from "./alas-logo.png";
import geonexLogo from "./geonex-logo.png";
import { apiErrorMessage } from "./apiErrorMessage";

const LOGO = process.env.PUBLIC_URL + "/fs-black.png";
const LOGIN_BG = `linear-gradient(105deg, rgba(4,15,35,0.72) 0%, rgba(7,27,60,0.46) 48%, rgba(8,25,52,0.24) 100%), url(${busBg}) 64% center / cover no-repeat`;

const ICONS = {
  home: <><path d="M3 10.8 12 3l9 7.8"/><path d="M5.5 9.5V21h13V9.5M9.5 21v-7h5v7"/></>,
  route: <><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="6" r="2.5"/><path d="M8.5 18h2a3 3 0 0 0 3-3v-6a3 3 0 0 1 3-3"/></>,
  check: <><path d="M9 11.5 11.5 14 16 9"/><path d="M7 4h10a2 2 0 0 1 2 2v15H5V6a2 2 0 0 1 2-2Z"/><path d="M9 4V2h6v2M8 18h8"/></>,
  wrench: <><path d="M14.7 6.3a4 4 0 0 0-5 5L3.5 17.5a2.1 2.1 0 1 0 3 3l6.2-6.2a4 4 0 0 0 5-5l-2.4 2.4-3-3Z"/></>,
  more: <><circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none"/></>,
  key: <><circle cx="8" cy="15" r="4"/><path d="m11 12 8-8m-3 3 2 2m-5 1 2 2"/></>,
  logout: <><path d="M10 5H5v14h5M14 8l4 4-4 4m4-4H9"/></>,
  moto: <><circle cx="5.5" cy="16.5" r="3.5"/><circle cx="18.5" cy="16.5" r="3.5"/><path d="M5.5 16.5h6l3.5-6.5h-4.5M15 10l3.5 6.5M12.5 6h3l2 4"/></>,
  bus: <><rect x="4" y="3" width="16" height="16" rx="3"/><path d="M4 12h16M7 7h10M7 19v2m10-2v2"/><circle cx="8" cy="15.5" r="1" fill="currentColor" stroke="none"/><circle cx="16" cy="15.5" r="1" fill="currentColor" stroke="none"/></>,
  clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  users: <><circle cx="9" cy="8" r="3"/><path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 5.5a3 3 0 0 1 0 5.8M17 14a5 5 0 0 1 3.5 5"/></>,
  idcard: <><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="8" cy="11" r="2"/><path d="M5.5 16a3 3 0 0 1 5 0M13 10h5m-5 3h5"/></>,
  camera: <><path d="M4 7h3l1.5-2h7L17 7h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z"/><circle cx="12" cy="13" r="3.5"/></>,
  success: <><circle cx="12" cy="12" r="9"/><path d="m8 12 2.7 2.7L16.5 9"/></>,
  alert: <><circle cx="12" cy="12" r="9"/><path d="M12 7v6m0 4h.01"/></>,
  refresh: <><path d="M20 7v5h-5"/><path d="M4 17v-5h5"/><path d="M6.1 9a7 7 0 0 1 11.7-2.6L20 12M4 12l2.2 5.6A7 7 0 0 0 17.9 15"/></>,
  location: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></>,
  power: <><path d="M12 2v10"/><path d="M6.3 5.7a8 8 0 1 0 11.4 0"/></>,
  scan: <><path d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4"/><path d="M8 8h3v3H8zM14 8h2M14 11h2M8 14h2M8 17h2M13 14h3v3h-3z"/></>,
  arrow: <><path d="m9 18 6-6-6-6"/></>,
  back: <><path d="m15 18-6-6 6-6"/></>,
};

function Icon({ name, size = 22 }) {
  return (
    <svg className="ui-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

function GeoNexLockup({ text = "GeoNex", inverse = false }) {
  return (
    <span className={`geonex-lockup ${inverse ? "inverse" : ""}`}>
      <img src={geonexLogo} alt="" aria-hidden="true" />
      <span>{text}</span>
    </span>
  );
}

const NAV_ITEMS = [
  ["home", "home", "Inicio"],
  ["route", "route", "Recorrido"],
  ["preop", "check", "Preoperacional"],
  ["mant", "wrench", "Taller"],
  ["more", "more", "Más"],
];

function MainNavigation({ screen, onNavigate }) {
  return (
    <nav className="primary-nav" aria-label="Navegación principal">
      <div className="nav-brand">
        <span className="nav-logo"><img src={LOGO} alt="" /></span>
        <span><b>Mi Ruta</b><small>Flota Sugamuxi</small></span>
      </div>
      <div className="nav-items">
        {NAV_ITEMS.map(([id, icon, label]) => (
          <button key={id} className={`${screen === id ? "active" : ""} ${id === "preop" ? "nav-primary" : ""}`} onClick={() => onNavigate(id)} aria-current={screen === id ? "page" : undefined}>
            <Icon name={icon} />
            <span>{label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

function PageHeading({ eyebrow, title, text, action }) {
  return (
    <div className="page-heading">
      <div>
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {text && <p>{text}</p>}
      </div>
      {action}
    </div>
  );
}

function errMsg(e, fallback) {
  // OJO: detail puede ser un OBJETO ({code,message}) o un ARRAY (validación 422).
  // Devolverlo tal cual lo pintaba en JSX y tumbaba la app (React #31, pantalla
  // en blanco). apiErrorMessage siempre devuelve texto plano en español.
  return apiErrorMessage(e, fallback || "Ocurrió un error.");
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("es-CO", {
      timeZone: "America/Bogota",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

// ============================== App ==============================
export default function App() {
  const [screen, setScreen] = useState("boot");
  const [dispatch, setDispatch] = useState(null);
  const [vstatus, setVstatus] = useState(null);
  // Req #59: vencimiento de la licencia, para avisar en la pantalla principal.
  const [licencia, setLicencia] = useState(null);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState("");

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  }

  // Requerimiento #82: cuando el conductor no tiene operación, elige entre
  // los vehículos que tiene habilitados. Guarda cuál eligió para el formulario.
  const [vehiculoPreop, setVehiculoPreop] = useState(null);

  const loadHome = useCallback(async () => {
    setScreen("boot");
    try {
      const d = await getMyDispatch();
      setDispatch(d);
      // Si «Mis datos» falla, la jornada se muestra igual, solo sin el aviso.
      getMyProfile().then((p) => setLicencia(p?.license_expiration || null)).catch(() => setLicencia(null));
      if (d?.has_dispatch && d.vehicle_id) {
        try {
          setVstatus(await getVehicleStatus(d.vehicle_id));
        } catch {
          setVstatus(null);
        }
      } else {
        setVstatus(null);
      }
      setScreen("home");
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        setScreen("login");
      } else {
        setDispatch({ has_dispatch: false, error: errMsg(e) });
        setScreen("home");
      }
    }
  }, []);

  useEffect(() => {
    if (getToken()) loadHome();
    else setScreen("login");
  }, [loadHome]);

  function handleLogout() {
    logout();
    setDispatch(null);
    setVstatus(null);
    setScreen("login");
  }

  if (screen === "boot") {
    return (
      <div className="app">
        <div className="center" style={{ marginTop: "42vh" }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }
  if (screen === "login") return <Login onDone={loadHome} />;

  const name = dispatch?.driver_name || "Conductor";
  const mainScreens = ["home", "route", "preop", "mant", "more"];
  const showMainNavigation = mainScreens.includes(screen);
  const titles = {
    dallas: "Llave Dallas", form: "Preoperacional", result: "Resultado",
    mant: "Mantenimientos", mantForm: "Registrar mantenimiento", profile: "Mis datos",
  };
  const goBack = () =>
    screen === "result" ? loadHome()
      : screen === "mantForm" ? setScreen("mant")
      : screen === "form" ? setScreen("preop")
      : screen === "profile" ? setScreen("more")
      : setScreen("more");
  const openPreop = (vehicle) => {
    setVehiculoPreop(vehicle || null);
    setScreen("form");
  };

  return (
    <div className={`app ${showMainNavigation ? "has-main-nav" : "detail-screen"}`}>
      <div className="topbar">
        <div className="topbar-inner">
          {showMainNavigation ? (
            <>
              <span className="logo"><img src={LOGO} alt="FS" /></span>
              <div className="who">
                <div className="greet">Mi Ruta</div>
                <div className="name">Hola, {name}</div>
              </div>
              <button className="profile-button" onClick={() => setScreen("more")} aria-label="Abrir mi cuenta">
                {name.trim().charAt(0).toUpperCase() || "C"}
              </button>
            </>
          ) : (
            <>
              <button className="backbtn" onClick={goBack} aria-label="Atrás"><Icon name="back" /></button>
              <div className="who"><div className="name">{titles[screen] || ""}</div></div>
            </>
          )}
        </div>
      </div>

      <div className="app-frame">
        {showMainNavigation && <MainNavigation screen={screen} onNavigate={setScreen} />}
        <main className="content">
          {screen === "home" && (
            <Home
              dispatch={dispatch}
              vstatus={vstatus}
              licencia={licencia}
              onPreop={openPreop}
              onRoute={() => setScreen("route")}
              onDallas={() => setScreen("dallas")}
              onMant={() => setScreen("mant")}
            />
          )}
          {screen === "route" && <RouteView dispatch={dispatch} />}
          {screen === "preop" && <PreopStart dispatch={dispatch} vstatus={vstatus} onPreop={openPreop} />}
          {screen === "more" && <MoreView dispatch={dispatch} onProfile={() => setScreen("profile")} onDallas={() => setScreen("dallas")} onLogout={handleLogout} />}
          {screen === "profile" && <MyData />}
          {screen === "dallas" && <Dallas onBack={goBack} />}
          {screen === "mant" && <Mantenimientos onNuevo={() => setScreen("mantForm")} />}
          {screen === "mantForm" && (
            <MantenimientoForm
              onListo={(r) => {
                showToast(`Quedó registrado ${r.record_number || ""}: mantenimiento lo revisa.`);
                setScreen("mant");
              }}
              onCancel={() => setScreen("mant")}
            />
          )}
          {screen === "form" && (
            <Form
              dispatch={
                vehiculoPreop
                  ? {
                      ...dispatch,
                      vehicle_id: vehiculoPreop.vehicle_id,
                      plate: vehiculoPreop.plate,
                      vehicle_class: vehiculoPreop.vehicle_class,
                      // Punto 8 (12-oct-2026): el aviso de mantenimiento por km es del vehículo elegido.
                      mantenimiento_km: vehiculoPreop.mantenimiento_km || null,
                      route_name:
                        vehiculoPreop.vehicle_id === dispatch?.vehicle_id ? dispatch?.route_name : "",
                    }
                  : dispatch
              }
              onDone={(res) => { setResult(res); setScreen("result"); }}
              onCancel={() => setScreen("preop")}
            />
          )}
          {screen === "result" && <Result result={result} onBack={loadHome} />}
        </main>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

// ============================== Dallas · GeoNex ==============================
function geonexSessionCode(e) {
  const detail = e?.response?.data?.detail;
  return detail && typeof detail === "object" ? detail.code : "";
}

function parseBusToken(value) {
  const text = String(value || "").trim();
  const fromUrl = text.match(/\/bus\/([A-Za-z0-9]+)/);
  if (fromUrl) return fromUrl[1];
  return /^[A-Za-z0-9]{20,128}$/.test(text) ? text : "";
}

function getPhonePosition(tries = 2) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    let attempt = 0;
    let done = false;
    const fallback = setTimeout(() => {
      if (!done) { done = true; resolve(null); }
    }, 20000);
    const finish = (value) => {
      if (done) return;
      done = true;
      clearTimeout(fallback);
      resolve(value);
    };
    const locate = () => {
      attempt += 1;
      navigator.geolocation.getCurrentPosition(
        (p) => finish({
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
          accuracy_m: p.coords.accuracy,
        }),
        (error) => {
          if (error?.code === 1 || attempt >= tries) finish(null);
          else setTimeout(locate, 800);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 },
      );
    };
    locate();
  });
}

function Dallas() {
  const [session, setSession] = useState(null);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [locating, setLocating] = useState(false);
  const [pendingToken, setPendingToken] = useState("");
  const [confirmCheckout, setConfirmCheckout] = useState(false);
  const [message, setMessage] = useState(null);

  const resetSession = useCallback(async (notice) => {
    try { await disconnectGeoNex(); } catch (_) { /* La cookie puede haber vencido. */ }
    setSession({ connected: false, driver: null, active_bus: null });
    if (notice) setMessage({ kind: "warn", text: notice });
  }, []);

  const handleError = useCallback((e, fallback) => {
    if (geonexSessionCode(e)) {
      resetSession("Tu conexión con GeoNex venció. Ingresa de nuevo para continuar.");
      return;
    }
    setMessage({ kind: "warn", text: errMsg(e, fallback) });
  }, [resetSession]);

  const loadSession = useCallback(async () => {
    try {
      setSession(await getGeoNexSession());
    } catch (e) {
      if (geonexSessionCode(e)) await resetSession();
      else {
        setSession({ connected: false, driver: null, active_bus: null });
        setMessage({ kind: "warn", text: errMsg(e, "No se pudo consultar GeoNex.") });
      }
    }
  }, [resetSession]);

  useEffect(() => { loadSession(); }, [loadSession]);

  async function handleConnect(e) {
    e.preventDefault();
    setBusy(true); setMessage(null);
    try {
      await connectGeoNex(password);
      setPassword("");
      await loadSession();
    } catch (error) {
      setMessage({ kind: "warn", text: errMsg(error, "No se pudo conectar tu cuenta de GeoNex.") });
    } finally { setBusy(false); }
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    if (newPassword !== repeatPassword) {
      setMessage({ kind: "warn", text: "Las contraseñas no coinciden." });
      return;
    }
    setBusy(true); setMessage(null);
    try {
      await changeGeoNexPassword(newPassword);
      setNewPassword(""); setRepeatPassword("");
      await loadSession();
      setMessage({ kind: "ok", text: "Tu contraseña de GeoNex quedó actualizada." });
    } catch (error) { handleError(error, "No se pudo cambiar la contraseña."); }
    finally { setBusy(false); }
  }

  async function sendCheckin(token, position) {
    setBusy(true); setPendingToken(""); setMessage(null);
    try {
      const result = await geonexCheckin({ qr_token: token, ...(position || {}) });
      setSession((current) => ({
        ...current,
        active_bus: { device_id: result.device_id, plate: result.plate },
      }));
      const handover = result.previous_driver
        ? ` Se cerró automáticamente el turno de ${result.previous_driver}.`
        : "";
      setMessage({
        kind: result.verified ? "ok" : "warn",
        text: result.verified
          ? `Turno iniciado en ${result.plate || "el bus"}. Ubicación confirmada.${handover}`
          : `Turno iniciado en ${result.plate || "el bus"}, pero no se pudo confirmar la cercanía por GPS.${handover}`,
      });
    } catch (error) { handleError(error, "No se pudo iniciar el turno."); }
    finally { setBusy(false); }
  }

  async function handleDetected(value) {
    const token = parseBusToken(value);
    setScanning(false);
    if (!token) {
      setMessage({ kind: "warn", text: "El código leído no corresponde a un QR de bus GeoNex." });
      return;
    }
    setLocating(true); setMessage(null);
    const position = await getPhonePosition();
    setLocating(false);
    if (position) await sendCheckin(token, position);
    else setPendingToken(token);
  }

  async function handleCheckout() {
    setBusy(true); setMessage(null);
    try {
      await geonexCheckout();
      setSession((current) => ({ ...current, active_bus: null }));
      setConfirmCheckout(false);
      setMessage({ kind: "ok", text: "Turno terminado. El bus quedó liberado en GeoNex." });
    } catch (error) { handleError(error, "No se pudo terminar el turno."); }
    finally { setBusy(false); }
  }

  if (!session) return <div className="center"><div className="spinner" /></div>;

  if (session.enabled === false) {
    return (
      <>
        <PageHeading eyebrow={<GeoNexLockup text="GeoNex · Integración terminada" />} title="Llave Dallas" text="El flujo de GeoNex ya está incorporado en Mi Ruta." />
        <section className="dallas-protected">
          <span className="dallas-emblem geonex-emblem"><img src={geonexLogo} alt="GeoNex" /></span>
          <div><span className="dallas-free"><i /> Protección QA</span><h2>Sin afectar turnos reales</h2></div>
          <p>Este ambiente comparte el GeoNex productivo. Por seguridad, aquí puedes revisar la interfaz, pero iniciar o terminar un turno está deshabilitado.</p>
          <ul><li>Cámara y lectura del QR</li><li>Confirmación de cercanía por GPS</li><li>Relevo automático y cierre de turno</li></ul>
          <div className="banner info">Se habilita con la configuración controlada de producción, después de aprobar QA.</div>
        </section>
      </>
    );
  }

  if (!session.connected) {
    return (
      <>
        <PageHeading eyebrow={<GeoNexLockup text="GeoNex · Llave virtual" />} title="Conecta GeoNex" text="Usaremos tu misma cédula de Mi Ruta. Solo debes escribir la contraseña que te asignaron en GeoNex." />
        {message && <div className={`banner ${message.kind === "ok" ? "ok" : "warn"}`}>{message.text}</div>}
        <form className="dallas-login card" onSubmit={handleConnect}>
          <span className="dallas-emblem geonex-emblem"><img src={geonexLogo} alt="GeoNex" /></span>
          <h2>Llave Dallas virtual</h2>
          <p>La contraseña viaja cifrada hasta GeoNex y no se guarda en ALAS.</p>
          <label htmlFor="gx-password">Contraseña de GeoNex</label>
          <input id="gx-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder="Tu contraseña" />
          <button className="btn" disabled={busy || !password}>{busy ? "Conectando…" : "Conectar cuenta"}</button>
          <small>Si aún no tienes acceso, solicita a Operaciones que habilite tu conductor en GeoNex.</small>
        </form>
      </>
    );
  }

  if (session.driver?.must_change_password) {
    const mismatch = repeatPassword && newPassword !== repeatPassword;
    return (
      <>
        <PageHeading eyebrow={<GeoNexLockup text="GeoNex · Seguridad" />} title="Crea tu contraseña" text="Debes cambiar la contraseña temporal antes de usar tu llave virtual." />
        {message && <div className="banner warn">{message.text}</div>}
        <form className="dallas-login card" onSubmit={handlePasswordChange}>
          <span className="dallas-emblem geonex-emblem"><img src={geonexLogo} alt="GeoNex" /></span>
          <label htmlFor="gx-new-password">Nueva contraseña</label>
          <input id="gx-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
          <label htmlFor="gx-repeat-password">Repite la contraseña</label>
          <input id="gx-repeat-password" type="password" value={repeatPassword} onChange={(e) => setRepeatPassword(e.target.value)} autoComplete="new-password" />
          {mismatch && <div className="field-error">Las contraseñas no coinciden.</div>}
          <button className="btn" disabled={busy || newPassword.length < 4 || !repeatPassword || mismatch}>{busy ? "Guardando…" : "Guardar y continuar"}</button>
        </form>
      </>
    );
  }

  const activeBus = session.active_bus;
  return (
    <>
      <PageHeading eyebrow={<GeoNexLockup text="Conectado a GeoNex" />} title="Llave Dallas" text={`Conductor: ${session.driver?.name || "identificado"}`} />
      {message && <div className={`banner dallas-message ${message.kind === "ok" ? "ok" : "warn"}`}>{message.text}</div>}

      {activeBus ? (
        <section className="dallas-active">
          <span className="dallas-live"><i /> En servicio</span>
          <span className="dallas-bus-icon"><Icon name="bus" size={31} /></span>
          <small>Estás identificado en el bus</small>
          <strong>{activeBus.plate || "Bus activo"}</strong>
          <p>GeoNex está usando tu llave virtual como identificación del conductor.</p>
          {!confirmCheckout ? (
            <button className="btn dallas-stop" onClick={() => setConfirmCheckout(true)}><Icon name="power" size={19} /> Terminar turno</button>
          ) : (
            <div className="dallas-confirm">
              <b>¿Liberar este bus?</b>
              <span><button className="btn dallas-stop" disabled={busy} onClick={handleCheckout}>{busy ? "Liberando…" : "Sí, terminar"}</button><button className="btn ghost" onClick={() => setConfirmCheckout(false)}>Cancelar</button></span>
            </div>
          )}
        </section>
      ) : (
        <section className="dallas-ready">
          <span className="dallas-emblem"><Icon name="scan" size={31} /></span>
          <span className="dallas-free"><i /> Libre</span>
          <h2>Escanea el QR de tu bus</h2>
          <p>Acércate al vehículo. Comprobaremos tu ubicación y GeoNex iniciará el turno.</p>
          <button className="btn" disabled={busy || locating} onClick={() => setScanning(true)}><Icon name="camera" size={20} /> Abrir cámara</button>
        </section>
      )}

      {locating && <div className="dallas-locating"><div className="spinner" /><b>Confirmando tu ubicación…</b><span>Permite el acceso al GPS cuando el celular lo solicite.</span></div>}
      {pendingToken && (
        <div className="dallas-location-warning">
          <Icon name="location" size={24} />
          <div><b>No logramos obtener tu ubicación</b><p>Puedes activar el GPS y reintentar, o iniciar el turno sin confirmación de cercanía.</p></div>
          <button className="btn" onClick={() => handleDetected(pendingToken)}><Icon name="refresh" size={18} /> Reintentar</button>
          <button className="btn ghost" onClick={() => sendCheckin(pendingToken, null)}>Iniciar sin ubicación</button>
        </div>
      )}

      <button className="dallas-disconnect" onClick={() => resetSession("Cuenta de GeoNex desconectada.")}>Desconectar cuenta GeoNex</button>
      {scanning && <DallasQrScanner onDetected={handleDetected} onClose={() => setScanning(false)} />}
    </>
  );
}

function DallasQrScanner({ onDetected, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState("");
  const [manualValue, setManualValue] = useState("");

  useEffect(() => {
    let stream;
    let frame;
    let active = true;
    let lastRead = 0;
    const scan = (now) => {
      if (!active) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState >= 2 && now - lastRead > 110) {
        lastRead = now;
        const maxWidth = 720;
        const scale = Math.min(1, maxWidth / video.videoWidth);
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
        const context = canvas.getContext("2d", { willReadFrequently: true });
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(pixels.data, pixels.width, pixels.height, { inversionAttempts: "dontInvert" });
        if (code?.data && parseBusToken(code.data)) {
          active = false;
          onDetected(code.data);
          return;
        }
      }
      frame = requestAnimationFrame(scan);
    };
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (!active || !videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        frame = requestAnimationFrame(scan);
      } catch (_) {
        setError("No se pudo abrir la cámara. Revisa el permiso del navegador.");
      }
    })();
    return () => {
      active = false;
      if (frame) cancelAnimationFrame(frame);
      if (stream) stream.getTracks().forEach((track) => track.stop());
    };
  }, [onDetected]);

  function submitManual(e) {
    e.preventDefault();
    if (parseBusToken(manualValue)) onDetected(manualValue);
    else setError("Ese enlace o token no corresponde a un bus GeoNex.");
  }

  return (
    <div className="qr-scanner" role="dialog" aria-modal="true" aria-label="Escanear QR del bus">
      <div className="qr-scanner-head"><button onClick={onClose} aria-label="Cerrar"><Icon name="back" /></button><b>Escanea el QR del bus</b><GeoNexLockup text="GeoNex" inverse /></div>
      <div className="qr-stage">
        <video ref={videoRef} playsInline muted />
        <canvas ref={canvasRef} aria-hidden="true" />
        <div className="qr-frame"><i /><i /><i /><i /></div>
        <span>Centra el código dentro del recuadro</span>
      </div>
      {error && <div className="banner warn">{error}</div>}
      <form className="qr-manual" onSubmit={submitManual}>
        <label htmlFor="qr-manual-value">¿No abre la cámara? Pega el enlace del QR</label>
        <div><input id="qr-manual-value" value={manualValue} onChange={(e) => setManualValue(e.target.value)} placeholder="https://gps.geonex.com.co/bus/…" /><button className="btn compact">Usar</button></div>
      </form>
    </div>
  );
}

// ============================== Home (tablero) ==============================
// Req #59 (Harry, 11-oct-2026): con la licencia de conducción vencida la taquilla
// no deja escoger al conductor para despachar. Se le avisa a él mismo, para que no
// lo lea como un error del sistema. Vale hasta el día de su vencimiento.
function estadoLicencia(vence, hoy = new Date()) {
  if (!vence) return null;
  const fecha = new Date(`${String(vence).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(fecha.getTime())) return null;
  const dia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const dias = Math.round((fecha - dia) / 86400000);
  if (dias < 0) return { tipo: "vencida", dias };
  if (dias <= 30) return { tipo: "por_vencer", dias };
  return null;
}

function AvisoLicencia({ vence }) {
  const estado = estadoLicencia(vence);
  if (!estado) return null;
  if (estado.tipo === "vencida") {
    return (
      <div className="banner err" role="alert">
        <b>Tu licencia de conducción venció el {fmtFecha(vence)}.</b> Mientras Talento Humano no la actualice
        en el sistema, la taquilla no te puede escoger para despachar. No es un error del sistema: si ya la
        renovaste, sube la foto en «Mis datos» y avisa a Talento Humano.
      </div>
    );
  }
  return (
    <div className="banner warn">
      Tu licencia de conducción vence el {fmtFecha(vence)}
      {estado.dias === 0 ? " (hoy)" : ` (en ${estado.dias} día${estado.dias === 1 ? "" : "s"})`}. Renuévala a tiempo:
      con la licencia vencida la taquilla no te puede despachar.
    </div>
  );
}

function Home({ dispatch, vstatus, licencia, onPreop, onRoute, onDallas, onMant }) {
  if (!dispatch?.has_dispatch) {
    const habilitados = dispatch?.enabled_vehicles || [];
    const bloqueados = dispatch?.blocked_vehicles || [];

    if (dispatch?.error) {
      return <div className="banner err">{dispatch.error}</div>;
    }

    return (
      <>
        <PageHeading eyebrow="Hoy" title="Tu jornada" text="Consulta tu operación y gestiona el vehículo desde un solo lugar." />
        <AvisoLicencia vence={licencia} />
        <div className="empty-state">
          <div className="status-icon"><Icon name="clock" size={25} /></div>
          <div>
            <h2>Sin planilla asignada</h2>
            <p>{habilitados.length ? "Aún puedes revisar cualquiera de tus vehículos habilitados." : "No tienes una operación ni vehículos habilitados. Contacta a rodamiento."}</p>
          </div>
        </div>
        {habilitados.length > 0 && (
          <>
            <SectionTitle title="Vehículos disponibles" count={habilitados.length} />
            <VehicleList vehicles={habilitados} onSelect={onPreop} />
          </>
        )}
        <BlockedVehicleList vehicles={bloqueados} />
        <SectionTitle title="Accesos rápidos" />
        <div className="quick-grid">
          <QuickAction icon="wrench" title="Taller" detail="Registra un mantenimiento" onClick={onMant} />
          <QuickAction icon="key" title="Llave Dallas" detail="Identifícate con el QR" onClick={onDallas} />
        </div>
      </>
    );
  }

  const preopDone = vstatus?.has_valid_preoperational;
  const otrosVehiculos = (dispatch.enabled_vehicles || []).filter((v) => v.vehicle_id !== dispatch.vehicle_id);
  const assignedDriverNames = (dispatch.assigned_drivers || []).map((driver) => driver?.name).filter(Boolean);
  const driverNames = assignedDriverNames.length ? assignedDriverNames : [dispatch.driver_name || "—"];
  const vehicleIdentity = dispatch.internal_number
    ? `${dispatch.internal_number} · ${dispatch.plate || "Sin placa"}`
    : dispatch.plate || "—";

  return (
    <>
      <PageHeading eyebrow="Hoy" title="Tu jornada" text="Todo listo para continuar la operación." />
      <AvisoLicencia vence={licencia} />

      <section className="trip-hero">
        <div className="trip-status"><span className="dot" /> Operación en curso</div>
        <h2>{dispatch.route_name || "Operación asignada"}</h2>
        <div className="trip-meta">
          <div><Icon name="bus" /><span>{dispatch.internal_number ? "Interno / placa" : "Vehículo"}<b>{vehicleIdentity}</b></span></div>
          <div><Icon name="clock" /><span>Salida<b>{fmtTime(dispatch.initial_datetime)}</b></span></div>
          <div><Icon name="users" /><span>Pasajeros<b>{dispatch.passengers_total ?? 0}</b></span></div>
          <div className="trip-drivers"><Icon name="users" /><span>{driverNames.length === 1 ? "Conductor" : "Conductores"}<b>{driverNames.join(" · ")}</b></span></div>
        </div>
        <button className="trip-link" onClick={onRoute}>
          Ver recorrido completo <Icon name="arrow" size={18} />
        </button>
      </section>

      {dispatch.vehicle_can_operate === false ? (
        <div className="banner err">
          <b>Este vehículo no puede salir.</b> {dispatch.vehicle_block_reason}
          {" "}No se puede registrar la preoperacional hasta que se resuelva. Comunícate con rodamiento.
        </div>
      ) : (
        <button className={`preop-status ${preopDone ? "done" : "pending"}`} onClick={() => onPreop(null)}>
          <span className="status-icon"><Icon name="check" size={22} /></span>
          <span className="preop-copy">
            <b>{preopDone ? "Preoperacional completada" : "Preoperacional pendiente"}</b>
            <small>{preopDone ? "Puedes consultar o repetir la revisión" : "Revisa el vehículo antes de iniciar"}</small>
          </span>
          <Icon name="arrow" size={19} />
        </button>
      )}

      <SectionTitle title="Accesos rápidos" />
      <div className="quick-grid">
        <QuickAction icon="route" title="Recorrido" detail="Paradas y pasajeros" onClick={onRoute} />
        <QuickAction icon="wrench" title="Taller" detail="Registra un mantenimiento" onClick={onMant} />
        <QuickAction icon="key" title="Llave Dallas" detail="Identifícate con el QR" onClick={onDallas} />
      </div>

      {otrosVehiculos.length > 0 && (
        <>
          <SectionTitle title="Otros vehículos" count={otrosVehiculos.length} />
          <VehicleList vehicles={otrosVehiculos} onSelect={onPreop} />
        </>
      )}
      <BlockedVehicleList
        vehicles={(dispatch.blocked_vehicles || []).filter((v) => v.vehicle_id !== dispatch.vehicle_id)}
      />
    </>
  );
}

// Vehículos asignados que NO pueden salir (bloqueo de Mantenimiento o documentos
// vencidos). Se muestran sin botón, con el motivo, para que el conductor sepa
// por qué no los puede revisar.
function BlockedVehicleList({ vehicles }) {
  if (!vehicles?.length) return null;
  return (
    <>
      <SectionTitle title="No pueden salir" count={vehicles.length} />
      {vehicles.map((v) => (
        <div key={v.vehicle_id} className="banner err">
          <b>N.º {v.interno || "s/i"} · {v.plate || "Sin placa"}</b>
          <div>{v.motivo}</div>
        </div>
      ))}
    </>
  );
}

function SectionTitle({ title, count }) {
  return (
    <div className="section-title">
      <h2>{title}</h2>
      {count != null && <span>{count}</span>}
    </div>
  );
}

function QuickAction({ icon, title, detail, onClick, muted }) {
  return (
    <button className={`quick-action ${muted ? "muted" : ""}`} onClick={onClick}>
      <span className="quick-icon"><Icon name={icon} /></span>
      <span><b>{title}</b><small>{detail}</small></span>
      <Icon name="arrow" size={17} />
    </button>
  );
}

function VehicleList({ vehicles, onSelect, currentId }) {
  return (
    <div className="veh-list">
      {vehicles.map((v) => (
        <button key={v.vehicle_id} className="veh-item" onClick={() => onSelect(v)}>
          <span className="vehicle-icon"><Icon name={v.vehicle_class === "MOTOCICLETA" ? "moto" : "bus"} size={21} /></span>
          <span className="vehicle-copy">
            <b>N.º {v.interno || "s/i"}</b>
            <small>{v.plate || "Sin placa"}{v.vehicle_id === currentId ? " · En operación" : ""}</small>
            {v.mantenimiento_km?.pendiente && <small className="km-pendiente">Mantenimiento pendiente</small>}
          </span>
          <span className="veh-go">Revisar <Icon name="arrow" size={16} /></span>
        </button>
      ))}
    </div>
  );
}

// Vista superior usada por ALAS en Rastreo satelital; replica el marcador GeoNex.
const LIVE_BUS_TOP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 64" width="12" height="24" aria-hidden="true">
<ellipse cx="16" cy="58" rx="10" ry="2.5" fill="#000" opacity="0.12"/>
<rect x="4" y="4" width="24" height="52" rx="8" ry="8" fill="#fff" stroke="#2d3436" stroke-width="1.4"/>
<rect x="4" y="14" width="24" height="28" fill="__C1__" opacity="0.85"/>
<rect x="4" y="12" width="24" height="3" fill="__C2__" opacity="0.6"/>
<rect x="4" y="41" width="24" height="3" fill="__C2__" opacity="0.6"/>
<rect x="7" y="5" width="18" height="8" rx="5" fill="#2c3e50" opacity="0.8"/>
<rect x="8" y="49" width="16" height="5" rx="4" fill="#2c3e50" opacity="0.7"/>
<circle cx="8" cy="5" r="2" fill="#f1c40f" opacity="0.9"/>
<circle cx="24" cy="5" r="2" fill="#f1c40f" opacity="0.9"/>
<rect x="6" y="54" width="4" height="2" rx="1" fill="#e74c3c" opacity="0.85"/>
<rect x="22" y="54" width="4" height="2" rx="1" fill="#e74c3c" opacity="0.85"/>
<rect x="10" y="20" width="12" height="16" rx="3" fill="#ecf0f1" opacity="0.45"/></svg>`;

function liveBusIcon(course, moving, online) {
  const heading = Number.isFinite(Number(course)) ? Number(course) : 0;
  const colors = !online
    ? ["#64748b", "#94a3b8"]
    : moving
    ? ["#1c3aa0", "#3b6bdb"]
    : ["#39435f", "#5a6a8a"];
  const svg = LIVE_BUS_TOP_SVG.replace(/__C1__/g, colors[0]).replace(/__C2__/g, colors[1]);
  const pulse = moving ? '<span class="live-bus-pulse"></span>' : "";
  return L.divIcon({
    className: "live-bus-marker",
    iconSize: [16, 28],
    iconAnchor: [8, 14],
    html: `<span class="live-bus-top" style="transform:rotate(${Math.round(heading)}deg)">${pulse}<span class="live-bus-top-body">${svg}</span></span>`,
  });
}

function LiveVehicleMap({ location }) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const lastPlateRef = useRef("");
  const initialPointRef = useRef([location.latitude, location.longitude]);

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return undefined;
    const map = L.map(hostRef.current, { zoomControl: false }).setView(initialPointRef.current, 16);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 0);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []); // El mapa vive mientras la pantalla Recorrido está montada.

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const point = [location.latitude, location.longitude];
    const moving = !!location.is_online && !!location.ignition && Number(location.speed_kmh || 0) > 2;
    if (!markerRef.current) {
      markerRef.current = L.marker(point, {
        icon: liveBusIcon(location.course, moving, location.is_online),
        title: `Bus ${location.plate || "en operación"}`,
      }).addTo(map);
    } else {
      markerRef.current
        .setLatLng(point)
        .setIcon(liveBusIcon(location.course, moving, location.is_online));
    }
    if (lastPlateRef.current !== location.plate) map.setView(point, 16);
    lastPlateRef.current = location.plate;
  }, [location]);

  const center = () => mapRef.current?.setView(
    [location.latitude, location.longitude],
    Math.max(mapRef.current.getZoom(), 16),
  );

  return (
    <div className="live-map-wrap">
      <div ref={hostRef} className="live-map" aria-label={`Ubicación del bus ${location.plate || ""}`} />
      <button className="map-center" onClick={center} aria-label="Centrar el bus"><Icon name="location" size={19} /> Centrar</button>
    </div>
  );
}

function RouteView({ dispatch }) {
  const [expanded, setExpanded] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [location, setLocation] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState("");

  const refreshLocation = useCallback(async (quiet = false) => {
    if (!dispatch?.has_dispatch) return;
    if (!quiet) setGpsLoading(true);
    try {
      setLocation(await getMyLiveLocation());
      setGpsError("");
    } catch (e) {
      setGpsError(errMsg(e, "No se pudo actualizar la ubicación del bus."));
    } finally {
      if (!quiet) setGpsLoading(false);
    }
  }, [dispatch?.has_dispatch]);

  useEffect(() => {
    if (!dispatch?.has_dispatch) return undefined;
    refreshLocation();
    const timer = setInterval(() => refreshLocation(true), 15000);
    return () => clearInterval(timer);
  }, [dispatch?.has_dispatch, refreshLocation]);

  if (!dispatch?.has_dispatch) {
    return (
      <>
        <PageHeading eyebrow="Recorrido" title="Sin operación activa" text="Cuando tengas una planilla, aquí aparecerán las paradas y pasajeros." />
        <div className="empty-state"><div className="status-icon"><Icon name="route" /></div><p>No hay un recorrido para mostrar.</p></div>
      </>
    );
  }

  const itinerary = dispatch.itinerary || [];
  const active = itinerary.filter((s) => s.boarding > 0 || s.alighting > 0);
  const stops = showAll || active.length === 0 ? itinerary : active;
  const toggle = (i) => setExpanded((e) => ({ ...e, [i]: !e[i] }));

  return (
    <>
      <PageHeading eyebrow="Operación en curso" title={dispatch.route_name || "Tu recorrido"} text="Consulta los horarios, movimientos y pasajeros de cada parada." />
      <div className="route-summary">
        <span><Icon name="bus" size={18} /> {dispatch.plate || "—"}</span>
        <span><Icon name="clock" size={18} /> {fmtTime(dispatch.initial_datetime)}</span>
        <span><Icon name="users" size={18} /> {dispatch.passengers_total ?? 0} pasajeros</span>
      </div>

      <section className="gps-panel">
        <div className="gps-panel-head">
          <div><span className="gps-source"><i /><GeoNexLockup text="GPS en vivo · GeoNex" /></span><h2>Ubicación del bus</h2></div>
          <button onClick={() => refreshLocation()} disabled={gpsLoading} aria-label="Actualizar ubicación"><Icon name="refresh" size={18} /> {gpsLoading ? "Actualizando" : "Actualizar"}</button>
        </div>
        {location?.available ? (
          <>
            <LiveVehicleMap location={location} />
            <div className="gps-details">
              <div><small>Estado</small><b className={location.is_online ? "gps-online" : "gps-offline"}><i /> {location.is_online ? "Reportando" : "Sin conexión"}</b></div>
              <div><small>Velocidad</small><b>{Math.round(location.speed_kmh || 0)} km/h</b></div>
              <div><small>Encendido</small><b>{location.ignition ? "Sí" : "No"}</b></div>
              <div><small>Último reporte</small><b>{fmtTime(location.reported_at)}</b></div>
            </div>
            <div className="gps-place"><Icon name="location" size={18} /><span><small>Ubicación reportada</small><b>{location.place || "Identificando la ubicación…"}</b></span><a href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`} target="_blank" rel="noreferrer">Abrir mapa</a></div>
          </>
        ) : gpsLoading && !location ? (
          <div className="gps-loading"><div className="spinner" /> Consultando GeoNex…</div>
        ) : (
          <div className="gps-unavailable"><Icon name="location" size={24} /><span><b>Aún no hay una posición disponible</b><small>GeoNex mostrará el bus cuando el equipo GPS envíe su ubicación.</small></span></div>
        )}
        {gpsError && <div className="gps-error"><Icon name="alert" size={16} /> {gpsError}</div>}
      </section>

      {itinerary.length === 0 ? (
        <div className="empty-state"><p>Sin recorrido registrado.</p></div>
      ) : (
        <>
          <div className="segmented">
            <button className={!showAll ? "on" : ""} onClick={() => setShowAll(false)}>
              Con pasajeros
            </button>
            <button className={showAll ? "on" : ""} onClick={() => setShowAll(true)}>
              Todo el recorrido ({itinerary.length})
            </button>
          </div>
          <div className="card timeline">
            {stops.map((s, i) => {
              const hasBoarding = s.boarding > 0;
              const hasAlighting = s.alighting > 0;
              const hasMovement = hasBoarding || hasAlighting;
              const boardingPassengers = Array.isArray(s.passengers) ? s.passengers : [];
              const alightingPassengers = Array.isArray(s.alighting_passengers) ? s.alighting_passengers : [];
              const movementClass = hasBoarding && hasAlighting
                ? "passengers-mixed"
                : hasBoarding
                ? "passengers-boarding"
                : hasAlighting
                ? "passengers-alighting"
                : "no-passengers";
              const isOpen = !!expanded[`${showAll}-${i}`];
              const passed = !!s.actual;
              const dotClass = s.is_origin
                ? "origin"
                : s.is_destination
                ? "dest"
                : passed
                ? "passed"
                : s.boarding || s.alighting
                ? "active"
                : "";
              const dl = s.delay_min;
              const dlClass = dl == null ? "" : dl > 3 ? "late" : dl < -3 ? "early" : "ontime";
              const dlLabel = dl == null ? "" : dl > 3 ? `+${dl} min` : dl < -3 ? `${dl} min` : "a tiempo";
              return (
                <div className={`tl-row ${movementClass} ${isOpen ? "open" : ""}`} key={i}>
                  <div className="tl-time">{fmtTime(s.eta)}</div>
                  <div className="tl-track"><span className={`tl-dot ${dotClass} ${hasMovement ? "has-passengers" : ""}`} /></div>
                  <div className="tl-body">
                    <div
                      className={`tl-head ${hasMovement ? "clickable" : ""}`}
                      onClick={hasMovement ? () => toggle(`${showAll}-${i}`) : undefined}
                      onKeyDown={hasMovement ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggle(`${showAll}-${i}`);
                        }
                      } : undefined}
                      role={hasMovement ? "button" : undefined}
                      tabIndex={hasMovement ? 0 : undefined}
                      aria-expanded={hasMovement ? isOpen : undefined}
                    >
                      <span className="tl-name">
                        {s.terminal}
                        {s.is_origin && <span className="tl-tag">Salida</span>}
                        {s.is_destination && <span className="tl-tag dest">Destino</span>}
                      </span>
                      <span className="tl-badges">
                        {hasBoarding && <span className="tl-up" title={`${s.boarding} pasajeros suben`}><b>↑ {s.boarding}</b><small>suben</small></span>}
                        {hasAlighting && <span className="tl-down" title={`${s.alighting} pasajeros bajan`}><b>↓ {s.alighting}</b><small>bajan</small></span>}
                        {hasMovement && <span className="tl-chev">{isOpen ? "▲" : "▼"}</span>}
                      </span>
                    </div>
                    {s.actual && (
                      <div className="tl-real">
                        <span className="rdot" />
                        Pasó {fmtTime(s.actual)}
                        {dlLabel && <span className={`dl ${dlClass}`}>{dlLabel}</span>}
                      </div>
                    )}
                    {isOpen && (
                      <div className="tl-pax">
                        {hasBoarding && <div className="tl-pax-title boarding">↑ Suben {s.boarding}</div>}
                        {boardingPassengers.map((p, j) => (
                          <div className="tl-p" key={`boarding-${j}`}>
                            <span className="seat">{p.seat}</span>
                            <span className="pn">{p.name}</span>
                            <span className="pd">→ {p.destination}</span>
                          </div>
                        ))}
                        {hasAlighting && <div className="tl-pax-title alighting">↓ Bajan {s.alighting}</div>}
                        {alightingPassengers.length > 0 ? alightingPassengers.map((p, j) => (
                          <div className="tl-p alighting" key={`alighting-${j}`}>
                            <span className="seat">{p.seat}</span>
                            <span className="pn">{p.name}</span>
                            <span className="pd">← {p.origin}</span>
                          </div>
                        )) : hasAlighting && (
                          <div className="tl-pax-empty">Actualiza para consultar el detalle de quienes bajan.</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

function PreopStart({ dispatch, vstatus, onPreop }) {
  const available = new Map();
  if (dispatch?.has_dispatch && dispatch?.vehicle_id && dispatch?.vehicle_can_operate !== false) {
    available.set(dispatch.vehicle_id, { vehicle_id: dispatch.vehicle_id, plate: dispatch.plate, interno: dispatch.interno });
  }
  (dispatch?.enabled_vehicles || []).forEach((v) => available.set(v.vehicle_id, v));
  const vehicles = Array.from(available.values());
  const done = !!vstatus?.has_valid_preoperational;

  return (
    <>
      <PageHeading eyebrow="Seguridad" title="Revisión preoperacional" text="Elige el vehículo y completa la inspección antes de salir." />
      {dispatch?.has_dispatch && (
        <div className={`preop-callout ${done ? "done" : "pending"}`}>
          <span className="status-icon"><Icon name="check" /></span>
          <span><b>{done ? "Revisión vigente" : "Revisión pendiente"}</b><small>{done ? "El vehículo de tu operación ya fue revisado hoy." : "Hazla antes de iniciar el recorrido."}</small></span>
        </div>
      )}
      <SectionTitle title={vehicles.length === 1 ? "Vehículo disponible" : "Elige un vehículo"} count={vehicles.length || null} />
      {vehicles.length ? (
        <VehicleList vehicles={vehicles} onSelect={onPreop} currentId={dispatch?.vehicle_id} />
      ) : (
        <div className="empty-state"><p>No tienes vehículos habilitados. Contacta a rodamiento.</p></div>
      )}
      <BlockedVehicleList vehicles={dispatch?.blocked_vehicles || []} />
    </>
  );
}

function MoreView({ dispatch, onProfile, onDallas, onLogout }) {
  const name = dispatch?.driver_name || "Conductor";
  return (
    <>
      <PageHeading eyebrow="Cuenta" title="Más opciones" text="Tu información y herramientas adicionales." />
      <div className="account-card">
        <span className="account-avatar">{name.trim().charAt(0).toUpperCase() || "C"}</span>
        <span><b>{name}</b><small>Conductor · Flota Sugamuxi</small></span>
      </div>
      <div className="settings-list">
        <button onClick={onProfile}><span className="settings-icon"><Icon name="idcard" /></span><span><b>Mis datos</b><small>Cédula, licencia y documentos</small></span><Icon name="arrow" size={18} /></button>
        <button onClick={onDallas}><span className="settings-icon geonex-settings-icon"><img src={geonexLogo} alt="GeoNex" /></span><span><b>Llave Dallas</b><small>Identificación virtual con GeoNex</small></span><Icon name="arrow" size={18} /></button>
        <button className="danger-link" onClick={onLogout}><span className="settings-icon"><Icon name="logout" /></span><span><b>Cerrar sesión</b><small>Salir de Mi Ruta en este dispositivo</small></span><Icon name="arrow" size={18} /></button>
      </div>
      <div className="app-version">Mi Ruta · Portal de conductores</div>
    </>
  );
}

const DOCUMENT_SLOTS = [
  ["cedula_frente", "Cédula", "Frente"],
  ["cedula_reverso", "Cédula", "Reverso"],
  ["licencia_frente", "Licencia", "Frente"],
  ["licencia_reverso", "Licencia", "Reverso"],
];

function formatBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function MyData() {
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [profileData, documentData] = await Promise.all([getMyProfile(), getMyDocuments()]);
      setProfile(profileData);
      setDocuments(documentData);
    } catch (requestError) {
      setError(errMsg(requestError, "No pudimos cargar tus datos."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byType = Object.fromEntries(
    (documents?.slots || []).map((slot) => [slot.document_type, slot.document]),
  );

  const handleFile = async (documentType, selectedFile) => {
    if (!selectedFile) return;
    setBusy(documentType);
    setError("");
    setSuccess("");
    try {
      const prepared = await comprimir(selectedFile);
      if (prepared.size > 10 * 1024 * 1024) throw new Error("El archivo supera el máximo de 10 MB.");
      await uploadMyDocument(documentType, prepared);
      setSuccess("Documento enviado. Talento Humano ya puede consultarlo.");
      await load();
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo enviar el documento."));
    } finally {
      setBusy("");
    }
  };

  const handleView = async (document) => {
    const popup = window.open("", "_blank");
    if (popup) popup.opener = null;
    setBusy(`view:${document.filename}`);
    setError("");
    try {
      const blob = await downloadMyDocument(document.filename);
      const objectUrl = URL.createObjectURL(blob);
      if (popup) popup.location = objectUrl;
      else {
        const anchor = window.document.createElement("a");
        anchor.href = objectUrl;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (requestError) {
      if (popup) popup.close();
      setError(errMsg(requestError, "No se pudo abrir el documento."));
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async (documentType) => {
    if (!window.confirm("¿Quieres quitar este documento?")) return;
    setBusy(documentType);
    setError("");
    setSuccess("");
    try {
      await deleteMyDocument(documentType);
      setSuccess("Documento retirado.");
      await load();
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo quitar el documento."));
    } finally {
      setBusy("");
    }
  };

  if (!profile || !documents) {
    return error
      ? <div className="banner err">{error}<button className="btn ghost compact profile-retry" onClick={load}>Reintentar</button></div>
      : <div className="center"><div className="spinner" /></div>;
  }

  const fullName = [profile.first_name, profile.last_name, profile.second_last_name].filter(Boolean).join(" ");
  return (
    <>
      <PageHeading eyebrow="Cuenta" title="Mis datos" text="Consulta tu información y envía tus documentos a Talento Humano." />

      <section className="profile-summary">
        <div className="profile-summary-head">
          <span className="account-avatar">{fullName.charAt(0).toUpperCase() || "C"}</span>
          <span><b>{fullName}</b><small>CC {profile.document_number || "—"}</small></span>
        </div>
        <div className="profile-data-grid">
          <span><small>Licencia</small><b>{profile.license_number || "Sin registrar"}</b></span>
          <span><small>Categoría</small><b>{profile.license_category || "—"}</b></span>
          <span><small>Vencimiento</small><b>{fmtFecha(profile.license_expiration)}</b></span>
          <span><small>Teléfono</small><b>{profile.phone || "Sin registrar"}</b></span>
        </div>
      </section>

      <div className="banner info profile-privacy"><Icon name="success" size={18} /> Tus archivos se guardan de forma privada y solo tú y el personal autorizado pueden verlos.</div>
      {error && <div className="banner err">{error}</div>}
      {success && <div className="banner ok">{success}</div>}

      <div className="profile-doc-heading">
        <span><b>Documentos</b><small>{documents.completed} de {documents.total} cargados</small></span>
        <em>{documents.completed}/{documents.total}</em>
      </div>
      <div className="profile-doc-grid">
        {DOCUMENT_SLOTS.map(([documentType, title, side]) => {
          const document = byType[documentType];
          const isBusy = busy === documentType || busy === `view:${document?.filename}`;
          return (
            <article className={`profile-doc-slot ${document ? "is-ready" : ""}`} key={documentType}>
              <div className="profile-doc-title">
                <span><Icon name={document ? "success" : "idcard"} size={20} /></span>
                <div><b>{title}</b><small>{side}</small></div>
                <em>{document ? "Cargado" : "Pendiente"}</em>
              </div>
              {document ? (
                <div className="profile-doc-file">
                  <span>{document.extension?.toUpperCase() || "ARCHIVO"}{document.size_bytes ? ` · ${formatBytes(document.size_bytes)}` : ""}</span>
                  <div>
                    <button type="button" onClick={() => handleView(document)} disabled={isBusy}>Ver</button>
                    <button type="button" className="remove" onClick={() => handleDelete(documentType)} disabled={isBusy}>Quitar</button>
                  </div>
                </div>
              ) : <p>Toma una foto nítida, completa y sin reflejos.</p>}
              <label className={`profile-upload ${isBusy ? "disabled" : ""}`}>
                <Icon name="camera" size={18} /> {isBusy ? "Procesando…" : document ? "Reemplazar" : "Tomar o adjuntar"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  disabled={isBusy}
                  onChange={(event) => {
                    const selectedFile = event.target.files?.[0];
                    event.target.value = "";
                    handleFile(documentType, selectedFile);
                  }}
                />
              </label>
            </article>
          );
        })}
      </div>
      <div className="banner info profile-help">Si algún dato personal está incorrecto, contacta a Talento Humano para que actualicen tu ficha.</div>
    </>
  );
}

// ============================== Mantenimientos (req #97) ==============================
/* El conductor registra lo que se le hizo a su bus, con los campos del formato
   de hoja de vida y la foto de la factura, la orden o el arreglo. Queda por
   validar: un ingeniero lo aprueba antes de pasarlo a la hoja de vida. */

const VALIDACION_PL = {
  POR_VALIDAR: ["Por validar", "todo"],
  VALIDADO: ["Validado", "done"],
  DEVUELTO: ["Devuelto", "bad"],
};

const hoyBogota = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Bogota" });

function fmtFecha(iso) {
  if (!iso) return "—";
  try {
    // Una fecha sola («2026-09-22») se leía como medianoche en UTC y en Bogotá
    // caía al día anterior: la licencia que vence el 22 salía «21». Req #59.
    const valor = /^\d{4}-\d{2}-\d{2}$/.test(String(iso)) ? `${iso}T12:00:00-05:00` : iso;
    return new Date(valor).toLocaleDateString("es-CO", {
      timeZone: "America/Bogota", day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return "—";
  }
}

/* Fotos a JPEG de máximo 1600 px: una foto de celular pesa 4-8 MB y el envío
   completo no puede pasar de 10 MB. Los PDF se mandan tal cual. */
async function comprimir(file) {
  if (!file.type.startsWith("image/") || /heic|heif/i.test(file.type)) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((ok, mal) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = mal;
      i.src = url;
    });
    const escala = Math.min(1, 1600 / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * escala);
    canvas.height = Math.round(img.height * escala);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((ok) => canvas.toBlob(ok, "image/jpeg", 0.78));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function Mantenimientos({ onNuevo }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getMyMaintenance()
      .then(setData)
      .catch((e) => setError(errMsg(e, "No se pudieron cargar tus mantenimientos.")));
  }, []);

  if (error) return <div className="banner err" style={{ marginTop: 16 }}>{error}</div>;
  if (!data) return <div className="center"><div className="spinner" /></div>;

  const vehiculos = data.vehicles || [];
  const registros = data.records || [];

  return (
    <>
      <PageHeading
        eyebrow="Taller"
        title="Mantenimientos"
        text="Registra los trabajos del vehículo y consulta su validación."
        action={vehiculos.length > 0 ? <button className="btn accent compact" onClick={onNuevo}><Icon name="wrench" size={18} /> Nuevo registro</button> : null}
      />

      {vehiculos.length === 0 ? (
        <div className="banner warn">No tienes vehículos asignados. Contacta a rodamiento.</div>
      ) : null}

      <SectionTitle title="Mis registros" count={registros.length} />
      {registros.length === 0 ? (
        <div className="empty-state"><div className="status-icon"><Icon name="wrench" /></div><p>Aún no has registrado mantenimientos.</p></div>
      ) : (
        registros.map((r) => {
          const [estado, clase] = VALIDACION_PL[r.validation_status] || [r.validation_status, "soon"];
          return (
            <div key={r.id} className="card mant-item">
              <div className="mant-head">
                <span className="veh-num">N.º {r.interno || "s/i"}</span>
                <span className={`pill ${clase}`}>{estado}</span>
              </div>
              <div className="mant-sub">{r.record_number} · {fmtFecha(r.performed_at)} · {r.category}</div>
              <div className="mant-desc">{r.description}</div>
              {r.validation_status === "DEVUELTO" && r.validation_notes && (
                <div className="banner err" style={{ margin: "10px 0 0" }}>
                  Devuelto: {r.validation_notes}. Regístralo de nuevo con la corrección.
                </div>
              )}
            </div>
          );
        })
      )}
    </>
  );
}

const MANT_VACIO = {
  vehicle_id: "", performed_date: "", maintenance_type: "preventive", category: "",
  requires_action: "", description: "", mileage: "", provider_name: "", provider_nit: "",
  order_number: "", cost: "", parts_replaced: "",
};

function MantenimientoForm({ onListo, onCancel }) {
  const [catalogo, setCatalogo] = useState(null);
  const [f, setF] = useState({ ...MANT_VACIO, performed_date: hoyBogota() });
  const [fotos, setFotos] = useState([]); // [{ file, url }]
  const [error, setError] = useState("");
  const [duplicado, setDuplicado] = useState("");
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    getMyMaintenance()
      .then((d) => {
        setCatalogo(d);
        const v = d.vehicles || [];
        if (v.length === 1) setF((x) => ({ ...x, vehicle_id: String(v[0].vehicle_id) }));
      })
      .catch((e) => setError(errMsg(e, "No se pudo cargar el formulario.")));
  }, []);

  // Las miniaturas se liberan al salir, no en cada cambio de la lista.
  const vivas = useRef([]);
  useEffect(() => { vivas.current = fotos; }, [fotos]);
  useEffect(() => () => vivas.current.forEach((p) => p.url && URL.revokeObjectURL(p.url)), []);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  async function agregar(lista) {
    setError("");
    const nuevas = [];
    for (const file of Array.from(lista || [])) {
      const esPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
      if (!file.type.startsWith("image/") && !esPdf) {
        setError(`"${file.name}" no es una foto ni un PDF.`);
        continue;
      }
      const lista2 = await comprimir(file);
      nuevas.push({ file: lista2, url: lista2.type.startsWith("image/") ? URL.createObjectURL(lista2) : null });
    }
    setFotos((p) => [...p, ...nuevas].slice(0, 6));
  }

  function quitar(i) {
    setFotos((p) => {
      if (p[i]?.url) URL.revokeObjectURL(p[i].url);
      return p.filter((_, j) => j !== i);
    });
  }

  async function enviar(confirmar) {
    setError("");
    setDuplicado("");
    if (!f.vehicle_id) return setError("Elige el vehículo.");
    if (!f.category) return setError("Elige la categoría.");
    if (f.description.trim().length < 10) return setError("Describe el mantenimiento (mínimo 10 caracteres).");
    const km = parseInt(f.mileage, 10);
    if (!f.mileage || isNaN(km) || km < 0) return setError("Ingresa el kilometraje del vehículo.");
    if (!f.provider_name.trim()) return setError("Escribe el nombre del taller o proveedor.");
    if (fotos.length === 0) return setError("Adjunta al menos una foto de la factura, la orden o el arreglo.");
    const peso = fotos.reduce((s, p) => s + p.file.size, 0);
    if (peso > 9.5 * 1024 * 1024) return setError("Los archivos pesan demasiado. Quita alguno e intenta de nuevo.");

    const fd = new FormData();
    const valores = { ...f, mileage: String(km), cost: (f.cost || "").replace(/[^\d]/g, "") };
    Object.entries(valores).forEach(([k, v]) => {
      const t = String(v ?? "").trim();
      if (t) fd.append(k, t);
    });
    if (confirmar) fd.append("confirmar_duplicado", "true");
    fotos.forEach((p) => fd.append("evidencias", p.file, p.file.name));

    setEnviando(true);
    try {
      const r = await createDriverMaintenance(fd);
      onListo(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "POSIBLE_DUPLICADO") setDuplicado(d.message);
      else setError(errMsg(e, "No se pudo registrar el mantenimiento."));
    } finally {
      setEnviando(false);
    }
  }

  if (!catalogo) {
    return error
      ? <div className="banner err" style={{ marginTop: 16 }}>{error}</div>
      : <div className="center"><div className="spinner" /></div>;
  }

  const vehiculos = catalogo.vehicles || [];
  const tipos = catalogo.types || { preventive: "Preventivo", corrective: "Correctivo" };
  const requiere = catalogo.requires || ["DESMONTAR", "CAMBIAR", "ARREGLAR"];

  return (
    <>
      <PageHeading eyebrow="Nuevo registro" title="Trabajo de taller" text="Completa la información y adjunta una evidencia legible." />
      <div className="form-panel mant-form">
      <label>Vehículo *</label>
      <select value={f.vehicle_id} onChange={set("vehicle_id")}>
        <option value="">Elige el vehículo</option>
        {vehiculos.map((v) => (
          <option key={v.vehicle_id} value={v.vehicle_id}>
            N.º {v.interno || "s/i"} · {v.plate || "sin placa"}
          </option>
        ))}
      </select>

      <div className="mant-fila">
        <div>
          <label>Fecha *</label>
          <input type="date" max={hoyBogota()} value={f.performed_date} onChange={set("performed_date")} />
        </div>
        <div>
          <label>Kilometraje *</label>
          <input type="tel" inputMode="numeric" placeholder="Km del tablero" value={f.mileage} onChange={set("mileage")} />
        </div>
      </div>

      <label>Tipo de mantenimiento *</label>
      <div className="toggle">
        {Object.entries(tipos).map(([k, l]) => (
          <button
            type="button"
            key={k}
            className={f.maintenance_type === k ? "yes on" : ""}
            onClick={() => setF((x) => ({ ...x, maintenance_type: k }))}
          >
            {l}
          </button>
        ))}
      </div>

      <label>Categoría *</label>
      <select value={f.category} onChange={set("category")}>
        <option value="">Elige la categoría</option>
        {(catalogo.categories || []).map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>

      <label>¿Qué se requirió?</label>
      <div className="toggle">
        {requiere.map((k) => (
          <button
            type="button"
            key={k}
            className={f.requires_action === k ? "yes on" : ""}
            onClick={() => setF((x) => ({ ...x, requires_action: x.requires_action === k ? "" : k }))}
          >
            {k.charAt(0) + k.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <label>Descripción del mantenimiento *</label>
      <textarea
        value={f.description}
        onChange={set("description")}
        placeholder="Ej.: cambio de pastillas de freno delanteras y rectificación de discos"
      />

      <label>Repuestos o piezas</label>
      <textarea style={{ minHeight: 48 }} value={f.parts_replaced} onChange={set("parts_replaced")} placeholder="Opcional" />

      <label>Taller o proveedor *</label>
      <input value={f.provider_name} onChange={set("provider_name")} placeholder="Nombre del taller o contratista" />

      <div className="mant-fila">
        <div>
          <label>NIT o cédula</label>
          <input inputMode="numeric" value={f.provider_nit} onChange={set("provider_nit")} placeholder="Opcional" />
        </div>
        <div>
          <label>N.º de orden o factura</label>
          <input value={f.order_number} onChange={set("order_number")} placeholder="Opcional" />
        </div>
      </div>

      <label>Costo</label>
      <input type="tel" inputMode="numeric" value={f.cost} onChange={set("cost")} placeholder="Opcional" />

      <label>Evidencia * <span className="mant-hint">factura, orden o foto del arreglo</span></label>
      <button
        type="button"
        className="btn ghost"
        onClick={() => inputRef.current?.click()}
        disabled={enviando || fotos.length >= 6}
      >
        <Icon name="camera" size={18} /> Tomar o adjuntar foto
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        style={{ display: "none" }}
        onChange={(e) => { agregar(e.target.files); e.target.value = ""; }}
      />
      {fotos.length > 0 && (
        <div className="mant-fotos">
          {fotos.map((p, i) => (
            <div key={i} className="mant-foto">
              {p.url ? <img src={p.url} alt="" /> : <span>PDF</span>}
              <button type="button" onClick={() => quitar(i)} disabled={enviando} aria-label="Quitar">✕</button>
            </div>
          ))}
        </div>
      )}

      {duplicado && (
        <div className="banner warn" style={{ marginTop: 16 }}>
          {duplicado}
          <button className="btn ghost" style={{ marginTop: 10 }} onClick={() => enviar(true)} disabled={enviando}>
            Sí, es otro trabajo: registrarlo
          </button>
        </div>
      )}
      {error && <div className="banner err" style={{ marginTop: 16 }}>{error}</div>}

      <div className="banner info" style={{ marginTop: 16 }}>
        Queda por validar: un ingeniero de mantenimiento lo revisa antes de pasarlo a la hoja de vida.
      </div>
      <button className="btn accent" onClick={() => enviar(false)} disabled={enviando}>
        {enviando ? "Enviando…" : "Registrar mantenimiento"}
      </button>
      <button className="btn ghost" style={{ marginTop: 10 }} onClick={onCancel} disabled={enviando}>
        Cancelar
      </button>
      </div>
    </>
  );
}

// ============================== Login ==============================
function Login({ onDone }) {
  const [cedula, setCedula] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!cedula.trim() || !password.trim()) {
      setError("Ingresa tu cédula y contraseña.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await login(cedula, password);
      onDone();
    } catch (e2) {
      setError(
        e2?.response?.status === 401
          ? "Cédula o contraseña incorrecta."
          : errMsg(e2, "No se pudo iniciar sesión.")
      );
      setLoading(false);
    }
  }

  return (
    <div className="login-hero" style={{ background: LOGIN_BG }}>
      <div className="login-shell">
        <section className="login-copy">
          <div className="login-brand-row">
            <div className="login-brand">
              <span className="badge-logo"><img src={LOGO} alt="" /></span>
              <span><b>Mi Ruta</b><small>Flota Sugamuxi</small></span>
            </div>
            <span className="login-alas-brand" aria-label="ALAS">
              <img src={alasLogo} alt="" aria-hidden="true" />
              <strong>ALAS</strong>
            </span>
          </div>
          <div>
            <div className="login-kicker">Portal de conductores</div>
            <h1>Tu jornada,<br />en un solo lugar.</h1>
            <p>Consulta el recorrido, revisa tu vehículo y registra novedades desde el camino.</p>
          </div>
        </section>

        <form className="login-card" onSubmit={submit}>
          <div className="login-logo">
            <span className="badge-logo"><img src={LOGO} alt="Flota Sugamuxi" /></span>
            <div className="login-kicker">Acceso seguro</div>
            <h2>Bienvenido</h2>
            <div className="sub">Ingresa con tus datos de conductor</div>
          </div>

          {error && <div className="banner err">{error}</div>}

          <label>Cédula</label>
          <input
            type="tel" inputMode="numeric" autoComplete="username"
            value={cedula} onChange={(e) => setCedula(e.target.value)}
            placeholder="Tu número de cédula"
          />

          <label>Contraseña</label>
          <input
            type="password" autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Tu contraseña"
          />

          <button className="btn" style={{ marginTop: 24 }} disabled={loading}>
            {loading ? "Entrando…" : "Entrar a Mi Ruta"}
          </button>
          <div className="login-help">¿Problemas para ingresar? Contacta a rodamiento.</div>
        </form>
      </div>
    </div>
  );
}

// ============================== Form ==============================
function Form({ dispatch, onDone, onCancel }) {
  // Req #104: la moto de la empresa tiene su propio cuestionario y se responde
  // CONFORME / NO CONFORME; cada NO CONFORME pide qué pasa y admite foto.
  const esMoto = dispatch?.vehicle_class === "MOTOCICLETA";
  const SI = esMoto ? "Conforme" : "Sí";
  const NO = esMoto ? "No conforme" : "No";
  const [fotos, setFotos] = useState({}); // question_id → File
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [config, setConfig] = useState(null);
  const [groups, setGroups] = useState([]);
  const [mileage, setMileage] = useState("");
  const [responses, setResponses] = useState({});
  const [generalObs, setGeneralObs] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { config, groups } = await getPreoperationalForm(esMoto ? "daily_motorcycle" : "daily");
        if (!alive) return;
        setConfig(config);
        setGroups(groups);
        const init = {};
        groups.forEach((g) => g.questions.forEach((q) => { init[q.id] = { answer: "yes", observations: "" }; }));
        setResponses(init);
      } catch (e) {
        if (alive) setLoadError(errMsg(e, "No se pudo cargar el formulario."));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [esMoto]);

  const setAnswer = (qid, answer) => setResponses((r) => ({ ...r, [qid]: { ...r[qid], answer } }));
  const setObs = (qid, observations) => setResponses((r) => ({ ...r, [qid]: { ...r[qid], observations } }));

  function missingObs() {
    const m = [];
    groups.forEach((g) => g.questions.forEach((q) => {
      const r = responses[q.id];
      if ((q.is_blocking || esMoto) && r?.answer === "no" && !r?.observations?.trim()) m.push(q.id);
    }));
    return m;
  }

  async function submit() {
    setTouched(true);
    setError("");
    const km = parseInt(mileage, 10);
    if (!mileage || isNaN(km) || km < 0) { setError("Ingresa el kilometraje del vehículo."); return; }
    if (missingObs().length > 0) { setError("Hay fallas marcadas que requieren una observación."); return; }

    const payload = {
      vehicle_id: dispatch.vehicle_id,
      preoperational_id: config.id,
      driver_id: dispatch.driver_id,
      mileage: km,
      responses: Object.entries(responses).map(([qid, r]) => ({
        question_id: parseInt(qid, 10),
        answer: r.answer,
        observations: r.observations?.trim() || null,
      })),
      observations: generalObs.trim() || null,
      completed_by_user_id: getUserIdFromToken(),
    };

    setSubmitting(true);
    try {
      const res = await createPreoperational(payload);
      // La preoperacional ya quedó: si una foto no sube, se avisa pero no se
      // repite el envío (duplicaría la inspección). Mantenimiento la puede pedir.
      let fotosFallidas = 0;
      for (const d of res?.defects || []) {
        const file = fotos[d.question_id];
        if (!file || responses[d.question_id]?.answer !== "no") continue;
        try { await uploadDefectEvidence(d.id, file); } catch { fotosFallidas += 1; }
      }
      const failed = [];
      groups.forEach((g) => g.questions.forEach((q) => {
        const r = responses[q.id];
        if (q.is_blocking && r?.answer === "no") failed.push({ q: q.question_text, obs: r.observations });
      }));
      onDone({ ...res, failed, fotosFallidas });
    } catch (e) {
      setError(errMsg(e, "No se pudo enviar el preoperacional."));
      setSubmitting(false);
    }
  }

  if (loading) return <div className="center"><div className="spinner" /></div>;
  if (loadError) {
    return (
      <>
        <div className="banner err" style={{ marginTop: 16 }}>{loadError}</div>
        <button className="btn ghost" onClick={onCancel}>Volver</button>
      </>
    );
  }

  const missing = touched ? missingObs() : [];
  const totalQuestions = groups.reduce((total, group) => total + group.questions.length, 0);

  return (
    <>
      <div className="form-head">
        <span className="form-head-icon"><Icon name={esMoto ? "moto" : "bus"} size={25} /></span>
        <span className="form-head-copy">
          <small>{esMoto ? "Revisión motocicleta" : "Revisión diaria"}</small>
          <b>{dispatch.plate || "Vehículo"}</b>
          {dispatch.route_name && <span>{dispatch.route_name}</span>}
        </span>
        <span className="form-count">{totalQuestions} puntos</span>
      </div>

      {/* Punto 8 (12-oct-2026): mantenimiento por kilometraje. Solo avisa: no impide el preoperacional. */}
      {dispatch.mantenimiento_km?.pendiente && (
        <div className="banner warn km-aviso" role="alert">
          <b>Mantenimiento pendiente</b>
          <span>
            Este vehículo ya cumplió su mantenimiento tipo {dispatch.mantenimiento_km.tipo} (cada{" "}
            {Number(dispatch.mantenimiento_km.intervalo_km).toLocaleString("es-CO")} km): lleva{" "}
            {Number(dispatch.mantenimiento_km.recorridos).toLocaleString("es-CO")} km desde el último.
            Avísale a Mantenimiento.
          </span>
        </div>
      )}

      <div className="card mileage-card">
        <label>Kilometraje actual</label>
        <input
          type="tel" inputMode="numeric" value={mileage}
          onChange={(e) => setMileage(e.target.value.replace(/[^\d]/g, ""))}
          placeholder="Ej: 145230"
        />
      </div>

      {groups.map((g) => (
        <div className="card" key={g.id}>
          <div className="group-title">{g.title}</div>
          {g.questions.map((q) => {
            const r = responses[q.id] || { answer: "yes", observations: "" };
            const obsMissing = missing.includes(q.id);
            return (
              <div className="question" key={q.id}>
                <div className="q-text">{q.question_text}{q.is_blocking && <span className="req">*</span>}</div>
                <div className="toggle">
                  <button type="button" className={`yes ${r.answer === "yes" ? "on" : ""}`} onClick={() => setAnswer(q.id, "yes")}>{SI}</button>
                  <button type="button" className={`no ${r.answer === "no" ? "on" : ""}`} onClick={() => setAnswer(q.id, "no")}>{NO}</button>
                </div>
                {r.answer === "no" && (
                  <textarea
                    className={obsMissing ? "err" : ""}
                    value={r.observations}
                    onChange={(e) => setObs(q.id, e.target.value)}
                    placeholder={q.is_blocking || esMoto ? "Describe la falla (obligatorio)" : "Observación (opcional)"}
                  />
                )}
                {esMoto && r.answer === "no" && (
                  <label className="btn ghost preop-foto">
                    <Icon name="camera" size={18} />
                    {fotos[q.id] ? ` Foto: ${fotos[q.id].name}` : " Tomar o adjuntar foto (opcional)"}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (file) setFotos((f) => ({ ...f, [q.id]: file }));
                      }}
                    />
                  </label>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="card">
        <label>Observaciones generales (opcional)</label>
        <textarea value={generalObs} onChange={(e) => setGeneralObs(e.target.value)} placeholder="Algo más que reportar…" />
      </div>

      {error && <div className="banner err">{error}</div>}

      <button className="btn accent" onClick={submit} disabled={submitting}>
        {submitting ? "Enviando…" : "Enviar preoperacional"}
      </button>
      <button className="btn ghost" style={{ marginTop: 10 }} onClick={onCancel} disabled={submitting}>Cancelar</button>
    </>
  );
}

// ============================== Result ==============================
function Result({ result, onBack }) {
  const approved = result?.status === "approved";
  return (
    <div className="center">
      <div className={`icon-big ${approved ? "ok" : "bad"}`}><Icon name={approved ? "success" : "alert"} size={36} /></div>
      <div className={`result-title ${approved ? "ok" : "bad"}`}>{approved ? "Aprobado" : "Rechazado"}</div>
      <div className="result-sub">
        {approved
          ? "Preoperacional registrado. Ya puedes salir."
          : "El vehículo tiene fallas que impiden el despacho. Repórtalo en la oficina."}
      </div>

      {result?.record_number && (
        <div className="banner info" style={{ marginTop: 16 }}>Registro N° <b>{result.record_number}</b></div>
      )}

      {result?.fotosFallidas > 0 && (
        <div className="banner err" style={{ marginTop: 16 }}>
          La inspección quedó registrada, pero {result.fotosFallidas === 1 ? "una foto no se pudo subir" : `${result.fotosFallidas} fotos no se pudieron subir`}.
          Envíala a Mantenimiento.
        </div>
      )}

      {!approved && result?.failed?.length > 0 && (
        <div className="failed-list">
          {result.failed.map((f, i) => (
            <div className="item" key={i}>
              <div className="q">{f.q}</div>
              {f.obs && <div className="obs">{f.obs}</div>}
            </div>
          ))}
        </div>
      )}

      <button className="btn" style={{ marginTop: 22 }} onClick={onBack}>Volver al inicio</button>
    </div>
  );
}
